import React, { useEffect, useMemo, useState } from "react";

import { getTpmSummaryReport } from "../../../../services/features/reports.service";
import {
  KpiRow,
  KpiTile,
  ReportGrid,
  ChartCard,
  EmptyNote,
  ReportBar,
  ReportDonut,
  SERIES,
  BRAND,
  OK_COLOR,
  num,
  pct,
  barData,
  barOptions,
  donutData,
  donutOptions,
  allZero,
} from "./reportShared";

// Report 1 — Site Visit Coverage & Progress. How many visits were targeted,
// how many are done, and where the rest sit in the pipeline.
const CoverageProgressReport = ({ onReady, onLoadingChange }) => {
  const projectId = localStorage.getItem("currentProjectID");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    getTpmSummaryReport(
      { projectId },
      (res) => {
        setData(res?.data || null);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [projectId]);

  const svp = data?.siteVisitsProgress || {};
  const abl = data?.activityByLocation || {};

  const target = num(abl.plannedVisits);
  const completed = num(abl.completedVisits) || num(svp.visitCompleted);
  const pipeline =
    num(svp.prePlanningPhase) +
    num(svp.planning) +
    num(svp.dataCollection) +
    num(svp.dataCheck);
  const remaining = target > completed ? target - completed : pipeline;
  const postponed = num(abl.postponedCards);
  const completionRate = pct(completed, target || completed + pipeline);

  const kpis = useMemo(
    () => [
      { label: "Target visits", value: target, color: SERIES[1] },
      { label: "Completed", value: completed, color: OK_COLOR },
      { label: "Completion rate", value: completionRate, suffix: "%", color: BRAND },
      { label: "In progress", value: pipeline, color: SERIES[0] },
      { label: "Postponed", value: postponed, color: SERIES[6] },
    ],
    [target, completed, completionRate, pipeline, postponed]
  );

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);
  useEffect(() => {
    if (!loading) onReady?.(kpis);
  }, [loading, kpis, onReady]);

  if (loading) return null;

  const stageRows = [
    { label: "Pre-planning", value: svp.prePlanningPhase },
    { label: "Planning", value: svp.planning },
    { label: "Data collection", value: svp.dataCollection },
    { label: "Data check", value: svp.dataCheck },
    { label: "Completed", value: svp.visitCompleted },
  ];
  const doneRows = [
    { label: "Completed", value: completed, color: OK_COLOR },
    { label: "Remaining", value: remaining, color: "#E6E1E4" },
  ];
  const plannedRows = [
    { label: "Target", value: target, color: SERIES[1] },
    { label: "Completed", value: completed, color: OK_COLOR },
  ];

  return (
    <>
      <KpiRow>
        {kpis.map((k) => (
          <KpiTile key={k.label} {...k} />
        ))}
      </KpiRow>

      <ReportGrid>
        <ChartCard title="Visits by stage" hint="Full pipeline">
          {allZero(stageRows) ? (
            <EmptyNote />
          ) : (
            <ReportBar data={barData(stageRows, SERIES)} options={barOptions(false)} />
          )}
        </ChartCard>

        <ChartCard title="Completed vs remaining">
          {allZero(doneRows) ? (
            <EmptyNote />
          ) : (
            <ReportDonut data={donutData(doneRows)} options={donutOptions} />
          )}
        </ChartCard>

        <ChartCard title="Target vs completed">
          {allZero(plannedRows) ? (
            <EmptyNote />
          ) : (
            <ReportBar data={barData(plannedRows, SERIES)} options={barOptions(true)} />
          )}
        </ChartCard>
      </ReportGrid>
    </>
  );
};

export default CoverageProgressReport;
